import React, {
    FC,
    useState,
    ReactElement,
    useEffect,
    ReactNode,
    Children,
    MouseEventHandler
} from 'react';
import {
    Box,
    BoxProps,
    Divider,
    List,
    ListItem,
    ListProps,
    ListItemProps,
    Popover,
    PopoverProps,
    Typography,
    withStyles,
    useMediaQuery,
    Paper,
    Grow
} from '@material-ui/core';
import root from 'window-or-global';
import {useTheme} from '@material-ui/core/styles';
import {makeStyles} from '@material-ui/core/styles';
import ChevronRight from '@material-ui/icons/ChevronRight';
import {FMD_BREAKPOINT_POPOVER_WIDTH, LG_BREAKPOINT_POPOVER_WIDTH} from '../constants';

// @ts-ignore
import {Theme} from '@epic-ui/epic';
import TabPanel from './TabPanel';
import Category, {CategoryProps} from './Category';
import CategoryContentButton from './CategoryContentButton';
import {getLtrPopoverPosition, getRtlPopoverPosition} from './utils';
import {getDirectionStyles} from '../../utils/ThemeUtils';

const StyledList = withStyles<string, undefined, ListProps & {component: string}>(theme => ({
    root: {
        width: '100%',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),

        [theme.breakpoints.up('fmd')]: {
            width: 180,
            minWidth: 180
        }
    }
}))(List);

const StyledListItem = withStyles<string, undefined, ListItemProps>(theme => ({
    root: {
        display: 'flex',
        justifyContent: 'space-between',
        color: theme.palette.text.secondary,

        '&:active': {
            color: theme.palette.text.primary
        },
        '&.Mui-selected': {
            color: theme.palette.text.primary
        },
        // Open options icon
        '& svg': {
            fontSize: '1.2rem',
            visibility: 'visible',
            position: 'absolute',

            right: getDirectionStyles(theme.direction)(15, 'unset'),
            left: getDirectionStyles(theme.direction)('unset', 15),
            transform: getDirectionStyles(theme.direction)('none', 'rotate(180deg)'),

            [theme.breakpoints.up('fmd')]: {
                visibility: 'hidden'
            }
        }
    }
}))(ListItem);

export const StyledPopover = withStyles((theme: Theme) => ({
    paper: {
        marginTop: theme.spacing(1),
        paddingBottom: theme.spacing(2),
        backgroundColor: theme.palette.background.overlays,

        width: `calc(100% - ${theme.spacing(8)}px)`,

        [theme.breakpoints.up('fsm')]: {
            width: `calc(100% - ${theme.spacing(24)}px)`
        },
        [theme.breakpoints.up('fmd')]: {
            width: 'auto'
        }
    }
}))(Popover);

export interface BoundingClientRectProps {
    top: number;
    left: number;
    right: number;
    height: number;
}

const useStyles = makeStyles<Theme, BoundingClientRectProps>(theme => ({
    overlay: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 1
    },
    paper: {
        position: 'absolute',
        zIndex: 2,
        // @ts-ignore
        backgroundColor: theme.palette.background.overlays,

        width: `calc(100% - ${theme.spacing(8)}px)`,
        top: p => p.top + p.height + 8,
        left: p => p.left,

        [theme.breakpoints.up('fsm')]: {
            width: `calc(100% - ${theme.spacing(24)}px)`
        },
        [theme.breakpoints.up('fmd')]: {
            width: FMD_BREAKPOINT_POPOVER_WIDTH,
            left: p =>
                getDirectionStyles(theme.direction)(
                    getLtrPopoverPosition(p.left, p.right, 400, FMD_BREAKPOINT_POPOVER_WIDTH),
                    getRtlPopoverPosition(p.left, p.right, 600, FMD_BREAKPOINT_POPOVER_WIDTH)
                )
        },
        [theme.breakpoints.up('lg')]: {
            width: LG_BREAKPOINT_POPOVER_WIDTH,
            left: p =>
                getDirectionStyles(theme.direction)(
                    p.left,
                    getRtlPopoverPosition(p.left, p.right, 600, LG_BREAKPOINT_POPOVER_WIDTH)
                )
        },
        [theme.breakpoints.up('xl')]: {
            left: p => p.left
        },

        '& [class*=pickerView]': {
            minWidth: '100%',
            maxWidth: '100%',
            minHeight: 250
        }
    }
}));

export interface CatalogDropdownProps extends Omit<PopoverProps, 'onChange'> {
    value: number;
    children: ReactNode;
    ContentBoxProps?: BoxProps;
    ListProps?: ListProps;
    hideCategoryContentButton?: boolean;
    onChange: (event: MouseEventHandler<HTMLDivElement>, value: number, label: string) => void;
    onClose?: () => void;
}

export interface CatalogDropdownComponent extends FC<CatalogDropdownProps> {
    Category: FC<CategoryProps>;
}

const CategorizedDropdown: CatalogDropdownComponent = ({
    children,
    value,
    onChange,
    ListProps,
    ContentBoxProps,
    hideCategoryContentButton,
    open,
    anchorEl,
    onClose
}: CatalogDropdownProps) => {
    const childrenArray = Children.toArray(children as ReactElement<CategoryProps>).filter(
        child => child.props && child.props.label
    );

    const [isCategoryContentActive, setIsCategoryContentActive] = useState(false);

    const [boundingClientRect, setBoundingClientRect] = useState<BoundingClientRectProps>({
        top: 0,
        left: 0,
        right: 0,
        height: 0
    });

    const classes = useStyles(boundingClientRect);

    const tabs = childrenArray.map(child => child.props.label);

    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));

    const onCategoryChange = (event, index, label) => {
        setIsCategoryContentActive(true);
        onChange(event, index, label);
    };

    useEffect(() => {
        if (open && anchorEl) {
            // @ts-ignore
            const {left = 0, right = 0, height = 0} = anchorEl.getBoundingClientRect();
            const top = anchorEl['offsetTop'];

            setBoundingClientRect({top, left, right, height});
        }
    }, [open]);

    useEffect(() => {
        const handler = event => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        root.addEventListener('keydown', handler);

        return () => {
            root.removeEventListener('keydown', handler);
        };
    }, [open]);

    return (
        <>
            <div className={open ? classes.overlay : undefined} onClick={onClose} />
            <Grow in={open} style={{transformOrigin: '0 0 0'}} {...(open ? {timeout: 300} : {})}>
                <Paper className={classes.paper} elevation={8}>
                    <Box display="flex">
                        {/* Categories */}
                        {(isFmdBreakpoint || !isCategoryContentActive) && (
                            <>
                                <StyledList component="div" {...ListProps}>
                                    {tabs.map((label, index) => (
                                        <StyledListItem
                                            button
                                            key={label}
                                            selected={value === index}
                                            onClick={event =>
                                                onCategoryChange(event, index, label)
                                            }>
                                            <Typography variant="body1">{label}</Typography>
                                            <ChevronRight />
                                        </StyledListItem>
                                    ))}
                                </StyledList>

                                <Divider flexItem orientation="vertical" />
                            </>
                        )}
                        {/* Content */}
                        {(isFmdBreakpoint || isCategoryContentActive) && (
                            <Box mt={2} mb={2} {...ContentBoxProps} width="100%">
                                {tabs.map((label, index) => (
                                    <TabPanel key={label} index={index} value={value}>
                                        {!isFmdBreakpoint && !hideCategoryContentButton && (
                                            <CategoryContentButton
                                                onClick={() => setIsCategoryContentActive(false)}
                                                title={label}
                                            />
                                        )}
                                        {childrenArray[index]}
                                    </TabPanel>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Grow>
        </>
    );
};

CategorizedDropdown.Category = Category;

export default CategorizedDropdown;
